import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";

import { getCategoryDetails, updateCategory } from "../../../../../services/Admin/MenuApi";
import { resolveImageUrl } from "../../../../../services/indexMediaBaseUrl";

const EditeCategoryDialog = ({ open, onClose, categoryId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(1);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchDetails = async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await getCategoryDetails(id);
      const data = res?.data || null;
      if (data) {
        setName(data.name || "");
        setDescription(data.description || "");
        setStatus(Number(data.status) ? 1 : 0);
        setImageFile(null);
        setPreviewUrl(data.image ? resolveImageUrl(data.image) : null);
      }
    } catch (err) {
      setError(err?.message || "فشل في جلب بيانات الفئة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && categoryId) {
      fetchDetails(categoryId);
    }
    if (!open) {
      // clear local state when closed
      setError("");
      setSuccess("");
      setImageFile(null);
      setPreviewUrl(null);
    }
  }, [open, categoryId]);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setImageFile(f || null);
    if (f) setPreviewUrl(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await updateCategory({
        id: categoryId,
        name,
        name_en: "",
        description,
        description_en: "",
        image: imageFile,
        status,
      });

      setSuccess(res?.message || "تم التحديث بنجاح");
      if (typeof onUpdated === "function") onUpdated(res?.data || null);
      // close after successful update
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(err?.message || "فشل في تحديث الفئة");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // revoke preview URL if created
    if (previewUrl && imageFile) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (e) {}
    }
    if (typeof onClose === "function") onClose();
  };

  return (
    <Dialog open={Boolean(open)} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>تعديل الفئة</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, mt: 1 }}>
            {success && <Alert severity="success">{success}</Alert>}

            <TextField label="الاسم" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label="الوصف" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={3} />

            <Box>
              <input accept="image/*" id="edit-category-image" type="file" onChange={handleFileChange} />
            </Box>

            {previewUrl ? (
              <Card sx={{ width: 120, mt: 1 }}>
                <CardMedia component="img" height="120" image={previewUrl} alt={name} />
              </Card>
            ) : null}

            <FormControlLabel control={<Switch checked={Boolean(Number(status))} onChange={(e) => setStatus(e.target.checked ? 1 : 0)} />} label="متاح" />

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button onClick={handleClose} color="inherit">إلغاء</Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={20} /> : "حفظ"}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions />
    </Dialog>
  );
};

export default EditeCategoryDialog;
